import styled from 'styled-components/macro';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchState } from 'react-zoom-pan-pinch';
import { createRef, useEffect } from 'react';
import { ReactComponent as ZoomBar } from '../../assets/zoom-bar.svg';
import React from 'react';
import { isMobileDevice } from './mobile-detection';

type PinchPanImgProps = {
    src: string;
    forwardedRef?: React.RefObject<HTMLImageElement>;
    onLoad?(): void;
}

type PinchPanProps = {
    children: React.ReactNode | ((props: ReactZoomPanPinchState) => React.ReactNode);
    staticChildren?: React.ReactNode;
    layer: 1 | 2 | 3;
};

const ScaleFactor = styled.div<{ scale: number; layer: 1 | 2 | 3 }>`
    position: absolute;
    z-index: ${(props) => {
        switch (props.layer) {
            case 1:
                return props.theme.zIndex.dropdown;
            case 2:
                return props.theme.zIndex.max;
            case 3:
                return props.theme.zIndex.modal;
        }
    }};
    top: 10px;
    right: 105px;
    margin-top: 5px;
    width: 150px;

    span {
        display: block;
        padding: 1px 4px 1px 9px;

        @media ${(props) => props.theme.screens.lg} {
            padding: 3px 8px 3px 12px;
        }
    }

    svg,path,rect,polygon {
        fill: ${(props) => props.theme.colors.white};
    }
    path {
        transform: ${(props) => `translateX(${(props.scale - 1) * 200}px)`};
    }

    #zoom-in, #zoom-out {
        content: '';
        display: inline-block;
        position: absolute;
        width: 40px;
        aspect-ratio: 1 / 1;
        cursor: pointer;
        top: -5px;
        z-index: ${(props) => props.theme.zIndex.max};
    }

    #zoom-out {
        left: -15px;
    }

    #zoom-in {
        right: -15px;
    }

    @media ${(props) => props.theme.screens.lg} {
        font-size: ${(props) => props.theme.typography.md};
        margin-top: 10px;
    }
`;

export const StyledImg = styled.img`
    display: block;
    max-width: 100dvw;
    max-height: 100dvh;
`

const ZoomBarWrapper = styled.div`
    position: relative;
    cursor: pointer;
`;

export const PinchPanImg = ({
    src,
    forwardedRef = createRef<HTMLImageElement>(),
    onLoad = () => {}
}: PinchPanImgProps) => {

    useEffect(() => {
        const img = forwardedRef?.current;

        if (img?.complete) {
            onLoad();
            return;
        }

        img?.addEventListener('load', onLoad);

        return () => {
            img?.removeEventListener('load', onLoad)
        }
    }, [])

    return (
        <TransformComponent>
            <StyledImg ref={forwardedRef} src={src} />
        </TransformComponent>
    )
}

export const PinchPan = (props: PinchPanProps) => {
    const [transformState, setTransformState] = React.useState<ReactZoomPanPinchState>({
        scale: 1,
        positionX: 0,
        positionY: 0,
        previousScale: 1,
    });

    const clickStartPosRef = React.useRef<{x: number, y: number} | null>(null);

    return (
        <TransformWrapper
            maxScale={2}
            disablePadding={true}
            doubleClick={{
                step: 10,
            }}
            wheel={{
                step: 20,
            }}
            onTransformed={(ref, state) => setTransformState({scale: state.scale, positionX: state.positionX, positionY: state.positionY, previousScale: transformState.scale})}
        >
            {({ zoomIn, zoomOut, setTransform, resetTransform }) => {

                return (
                    <>
                        {!isMobileDevice() && <ScaleFactor scale={transformState.scale} layer={props.layer}>
                            <div
                                id='zoom-out'
                                onClick={() => {
                                    {transformState.scale >= 1.25 ? zoomOut(0.25) : zoomOut(transformState.scale - 1)}
                                }}
                            />
                            <ZoomBarWrapper>
                                <ZoomBar />
                            </ZoomBarWrapper>
                            <div
                                id='zoom-in'
                                onClick={() => {
                                    {transformState.scale <= 1.75 ? zoomIn(0.25) : zoomIn(2 - transformState.scale)}
                                }}
                            />
                        </ScaleFactor>}
                        {props.staticChildren}
                        <TransformComponent >
                            <div
                                onMouseDown={(e) => { clickStartPosRef.current = { x: e.clientX, y: e.clientY }; }}
                                onClickCapture={(e) => {
                                    if (transformState.scale === 1) return;
                                    const start = clickStartPosRef.current;
                                    if (start && Math.abs(e.clientX - start.x) < 5 && Math.abs(e.clientY - start.y) < 5) {
                                        resetTransform();
                                    }
                                }}
                            >
                                {typeof props.children === 'function' ? props.children(transformState) : props.children}
                            </div>
                        </TransformComponent>
                    </>
                );
            }}
        </TransformWrapper>
    );
}
